/*
 * This unpublished material is proprietary to Vaticle.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of Vaticle.
 */

import { AsyncPipe, NgClass } from "@angular/common";
import { Component, Inject } from "@angular/core";
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialog, MatDialogContent, MatDialogRef, MatDialogTitle } from "@angular/material/dialog";
import { BehaviorSubject, Observable,
    ReplaySubject, Subject, combineLatest, filter, first, map, shareReplay, startWith, switchMap,
    tap} from "rxjs";
import {
    ButtonComponent, FormActionsComponent, FormComponent,
    FormOption, FormSelectComponent, ModalCloseButtonComponent, ModalComponent, requiredValidator,
    SpinnerComponent} from "typedb-cloud-framework";
import { Cluster, ClusterPrice, Frequency, SnapshotConfiguration, storageSizeToString } from "../../../concept/cluster";
import { ClusterApi } from "../../../service/cluster/cluster-api.service";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { ApplicationState } from "../../../service/application-state.service";
import { DialogResult } from "../../../service/dialog.service";
import { SnackbarService } from "../../../service/snackbar.service";
import { PartialWithUuid } from "../../../concept/base";
import {
    burstableTooltip,
    getPaymentOptions, machineTypeComputeString,
    MachineTypeOption,
    machineTypeToString,
    ProviderOptions, sortMachineType
} from "../../../concept/cluster-deployment-options";
import { MachineTypePipe } from "../machine-type.pipe";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatSelectModule } from "@angular/material/select";
import { MatSliderModule } from "@angular/material/slider";
import { MatTooltipModule } from "@angular/material/tooltip";
import { renderThousandthCentsAsUSD, toSentenceCase } from "../../../util";
import { TeamApi } from "../../../service/team/team-api.service";
import { PaymentDetailsModalComponent } from "../../team/payment-details-modal/payment-details-modal.component";

export type ClusterEditDialogData = { cluster: Cluster, currentStorageUsage: number };

const storageSliderValueToGBMap: { [key: number]: number } = {
    0: 10,
    1: 25,
    2: 50,
    3: 75,
    4: 100,
    5: 200,
    6: 400,
    7: 600,
    8: 800,
    9: 1000,
};
const gbToStorageSliderValueMap = Object.fromEntries(
    Object.entries(storageSliderValueToGBMap).map(([value, gb]) => [gb, value])
) as unknown as Record<number, number>
const frequencyOptions: FormOption<Frequency>[] = [
    { value: "disabled", viewValue: "Disabled" },
    { value: "hourly", viewValue: "Hourly" },
    { value: "daily", viewValue: "Daily" },
    { value: "weekly", viewValue: "Weekly" },
]
const unfilteredRetentionOptions: FormOption<number>[] = [
    { value: 7, viewValue: "7 days" },
    { value: 30, viewValue: "30 days" },
    { value: 365, viewValue: "365 days" },
]

@Component({
    selector: "tp-cluster-update-dialog",
    templateUrl: "./cluster-update-dialog.component.html",
    styleUrl: "./cluster-update-dialog.component.scss",
    imports: [
        AsyncPipe, FormsModule, ReactiveFormsModule, FormComponent, MatFormFieldModule, MatInputModule,
        MachineTypePipe, MatSelectModule, MatSliderModule, FormSelectComponent, ButtonComponent,
        ModalCloseButtonComponent, MatProgressBarModule, NgClass, SpinnerComponent, MatTooltipModule,
    ]
})
export class ClusterUpdateDialogComponent {
    readonly team = this.app.requireCurrentTeam();
    readonly isSubmitting$ = new Subject<boolean>();
    readonly form = this.formBuilder.nonNullable.group({
        storageSlider: [
            gbToStorageSliderValueMap[this.data.cluster.storageGB],
            [requiredValidator, Validators.min(gbToStorageSliderValueMap[this.data.cluster.storageGB])]
        ],
        machineType: [null as MachineTypeOption | null, [requiredValidator]],
        snapshotFrequency: [this.data.cluster.snapshotConfiguration.frequency, [requiredValidator]],
        snapshotRetentionDays: [this.data.cluster.snapshotConfiguration.retentionDays, [requiredValidator]],
    });
    readonly clusterDeploymentOptions$ = this.clusterApi.getClusterDeploymentOptions({
        teamUuid: this.team.uuid,
        providerID: this.data.cluster.provider.id,
        regionID: this.data.cluster.region.id,
    });
    readonly payableProviderOptions$ = new ReplaySubject<ProviderOptions[]>(1);
    machineTypes$: Observable<FormOption<MachineTypeOption>[]> = this.clusterDeploymentOptions$.pipe(
        map(x => x.providers[0].regions[0].machineTypes
            .sort(sortMachineType)
            .map(mt => ({ value: mt, viewValue: machineTypeToString(mt) }))
        )
    );
    machineTypes: FormOption<MachineTypeOption>[] = [];
    retentionOptions$ = new BehaviorSubject(unfilteredRetentionOptions);
    readonly frequencyDisabled$ = this.form.controls.snapshotFrequency.valueChanges.pipe(
        map((freq) => freq !== this.data.cluster.snapshotConfiguration.frequency && freq === 'disabled')
    );
    readonly clusterHasSnapshots$ = this.clusterApi.listSnapshots(this.data.cluster.uuid).pipe(map(snapshots => !!snapshots.length))
    readonly showRetention$ = combineLatest([
        this.clusterHasSnapshots$, this.form.controls.snapshotFrequency.valueChanges.pipe(startWith(undefined))
    ]).pipe(
        map(([hasSnapshots, snapshotFreq]) => {
            return (snapshotFreq && snapshotFreq != "disabled") || this.data.cluster.snapshotConfiguration.frequency != "disabled" || hasSnapshots;
        }),
        startWith(this.data.cluster.snapshotConfiguration.frequency != "disabled")
    );
    readonly reducedRetention$ = this.form.controls.snapshotRetentionDays.valueChanges.pipe((map((ret) =>
        ret! < this.data.cluster.snapshotConfiguration.retentionDays! ? ret : undefined
    )));

    readonly pricePerHourDetails$ = combineLatest([
        this.form.controls.machineType.valueChanges,
        this.form.controls.storageSlider.valueChanges,
    ]).pipe(
        startWith([null, null]),
        switchMap(([machineType, storageSlider]) => this.clusterApi.getClusterPriceThousandthCentsPerHour({
            cluster: {
                ...this.data.cluster,
                isFree: machineType?.isFree ?? this.data.cluster.isFree,
                machineType: machineType ?? this.data.cluster.machineType,
                storageGB: storageSlider ? storageSliderValueToGBMap[storageSlider] : this.data.cluster.storageGB,
                snapshotConfiguration: {
                    frequency: this.form.value.snapshotFrequency ?? this.data.cluster.snapshotConfiguration.frequency,
                    retentionDays: this.form.value.snapshotRetentionDays  ?? this.data.cluster.snapshotConfiguration.retentionDays
                },
            },
            teamUuid: this.team.uuid
        }).pipe(startWith<ClusterPrice | "loading">("loading"))),
        shareReplay(1),
    );
    isFree$ = this.pricePerHourDetails$.pipe(map(x => x != "loading" && x.running.thousandthCentsPerHour === 0));
    pricePerHourString$ = this.pricePerHourDetails$.pipe(map(x => {
        if (x === "loading") return undefined;
        else return this.pricePerHourToPerHourString(x.running.thousandthCentsPerHour);
    }));
    basePricePerHourString$ = this.pricePerHourDetails$.pipe(map(x => {
        if (x === "loading" || x.running.baseThousandthCentsPerHour == undefined || x.running.baseThousandthCentsPerHour == x.running.thousandthCentsPerHour) return undefined;
        else return this.pricePerHourToPerHourString(x.running.baseThousandthCentsPerHour!);
    }));
    snapshotPriceString$ = this.pricePerHourDetails$.pipe(
        map(x => {
            if (x === "loading" || x.snapshots == undefined) return undefined;
            else return this.pricePerHourToPerMonthString(x.snapshots.thousandthCentsPerHour);
        })
    );
    baseSnapshotPriceString$ = this.pricePerHourDetails$.pipe(
        map(x => {
            if (x === "loading" || x.snapshots == undefined || x.snapshots.baseThousandthCentsPerHour == undefined || x.snapshots.baseThousandthCentsPerHour == x.snapshots.thousandthCentsPerHour) return undefined;
            else return this.pricePerHourToPerMonthString(x.snapshots.baseThousandthCentsPerHour!);
        })
    );
    pricePerMonthString$ = this.pricePerHourDetails$.pipe(map(x => {
        if (x === "loading") return undefined;
        else return this.estimateMonthlyPrice({ frequency: this.form.value.snapshotFrequency!, retentionDays: this.form.value.snapshotRetentionDays! }, x);
    }));

    readonly previousPricePerHourDetails$ = this.clusterApi.getClusterPriceThousandthCentsPerHour({
        cluster: this.data.cluster,
        teamUuid: this.team.uuid
    }).pipe(shareReplay(1));
    readonly previousPricePerHourString$ = this.previousPricePerHourDetails$.pipe(map(x => this.pricePerHourToPerHourString(x.running.thousandthCentsPerHour)));
    readonly hourlyPriceTooltip$ = combineLatest([this.pricePerHourString$, this.previousPricePerHourString$]).pipe(
        filter(([a, b]) => a !== b),
        map(([_, prev]) => `Previously ${prev}`)
    );
    readonly previousPricePerMonthString$ = this.previousPricePerHourDetails$.pipe(map(x => this.estimateMonthlyPrice(
        { frequency: this.data.cluster.snapshotConfiguration.frequency!, retentionDays: this.data.cluster.snapshotConfiguration.retentionDays! }, x
    )));
    readonly monthlyPriceTooltip$ = combineLatest([this.pricePerMonthString$, this.previousPricePerMonthString$]).pipe(
        filter(([a, b]) => a !== b),
        map(([_, prev]) => `Previously ${prev}`)
    );
    checkoutShowMonthlyEstimate = false;
    previousStorageString = storageSizeToString(this.data.cluster.storageGB);
    previousComputeString = machineTypeComputeString(this.data.cluster.machineType);
    previousRetentionString = `${this.data.cluster.snapshotConfiguration.retentionDays} days`

    readonly isFreeTierSelected$ = this.form.controls.machineType.valueChanges.pipe(
        filter(value => !!value),
        map(value => value.isFree),
        startWith(this.data.cluster.isFree),
    );
    readonly hasUsablePaymentMethod$ = combineLatest([this.payableProviderOptions$, this.form.valueChanges]).pipe(
        map(([payable]) =>
            getPaymentOptions(payable, this.data.cluster.provider.id, this.data.cluster.region.id, this.form.value.machineType!.vendorId).length > 0
        )
    );
    readonly mustAddPaymentMethod$ = combineLatest([this.isFreeTierSelected$, this.hasUsablePaymentMethod$]).pipe(
        map(([isFreeTierSelected, hasUsablePaymentMethod]) => !isFreeTierSelected && !hasUsablePaymentMethod),
        shareReplay(1),
    );

    constructor(
        private dialogRef: MatDialogRef<ClusterUpdateDialogComponent, DialogResult>, private formBuilder: FormBuilder,
        @Inject(MAT_DIALOG_DATA) protected data: ClusterEditDialogData, private clusterApi: ClusterApi,
        private app: ApplicationState, private snackbar: SnackbarService, private teamApi: TeamApi, private dialog: MatDialog,
    ) {
        this.machineTypes$.subscribe(mts => this.machineTypes = mts.filter(mt => !mt.value.isFree || this.data.cluster.isFree));
        this.machineTypes$.pipe(first()).subscribe(() => {
            const machineType = this.machineTypes.find(mt => mt.value.vendorId === this.data.cluster.machineType.vendorId)!.value;
            this.form.patchValue({ machineType });
        });
        this.pricePerHourDetails$.subscribe();
        this.form.controls.snapshotFrequency.valueChanges.pipe(startWith(null)).subscribe((frequency) => {
            if (frequency === "hourly") {
                if (this.form.value.snapshotRetentionDays === 365) this.form.patchValue({ snapshotRetentionDays: 30 });
                this.retentionOptions$.next(unfilteredRetentionOptions.filter(x => x.value != 365));
            } else if (frequency === "weekly") {
                if (this.form.value.snapshotRetentionDays === 7) this.form.patchValue({ snapshotRetentionDays: 30 });
                this.retentionOptions$.next(unfilteredRetentionOptions.filter(x => x.value != 7));
            } else this.retentionOptions$.next(unfilteredRetentionOptions);
        });
        this.form.controls.machineType.valueChanges.subscribe((value) => {
            if (value?.isFree) this.form.patchValue({
                storageSlider: 0,
                snapshotFrequency: "disabled",
            });
        });
        this.form.controls.snapshotFrequency.valueChanges.subscribe((value) => {
            if (value != "disabled" && value != null) this.updateToPaidMachineType();
        });
        this.form.controls.storageSlider.valueChanges.subscribe((value) => {
            if (value != 0 && value != null) this.updateToPaidMachineType();
        });
        this.fetchPayableProviders(false);
    }

    readonly machineTypeFieldTitle$ = combineLatest([
        this.machineTypes$, this.form.controls.machineType.valueChanges.pipe(startWith(this.data.cluster.machineType))
    ]).pipe(map(([machineTypes, selected]) =>
        machineTypes.find(mt => selected?.vendorId === mt.value.vendorId)?.viewValue ?? "Select an option"
    ));

    get storageSliderCurrentValueToString(): string {
        if (this.form.value.storageSlider != null) return this.storageSliderValueToString(this.form.value.storageSlider!);
        else return "--";
    }

    storageSliderValueToString(rawValue: number): string {
        const gb = storageSliderValueToGBMap[rawValue];
        return storageSizeToString(gb);
    }

    buildUpdate(): PartialWithUuid<Cluster> {
        const value = this.form.value
        const cluster = this.data.cluster
        const newStorageGB = storageSliderValueToGBMap[value.storageSlider!];
        const newFrequency = this.form.value.snapshotFrequency;
        const newRetention = this.form.value.snapshotRetentionDays;
        const frequency = newFrequency !== this.data.cluster.snapshotConfiguration.frequency ? newFrequency : undefined;
        const retentionDays = newRetention !== this.data.cluster.snapshotConfiguration.retentionDays ? newRetention : undefined;
        const snapshotConfiguration: Partial<SnapshotConfiguration> | undefined = frequency || retentionDays ? { frequency, retentionDays } : undefined;
        const isFree = cluster.isFree ? false : undefined;
        return {
            uuid: cluster.uuid,
            storageGB: newStorageGB !== cluster.storageGB ? newStorageGB : undefined,
            machineType: this.machineTypeValueChanged ? value.machineType! : undefined,
            isFree,
            snapshotConfiguration,
        }
    }

    pricePerHourToPerHourString(pricePerHourThousandthCents: number): string {
        if (pricePerHourThousandthCents === 0) return `Free`;
        else return renderThousandthCentsAsUSD(pricePerHourThousandthCents);
    }

    pricePerHourToPerMonthString(pricePerHourThousandthCents: number): string {
        if (pricePerHourThousandthCents === 0) return `Free`;
        else return renderThousandthCentsAsUSD(pricePerHourThousandthCents * 720);
    }

    fetchPayableProviders(manualTrigger: boolean) {
        if (manualTrigger) this.isSubmitting$.next(true);
        this.teamApi.getPayableClusterDeploymentOptions(this.team.uuid).subscribe((payableProviders) => {
            this.payableProviderOptions$.next(payableProviders);
            this.isSubmitting$.next(false);
            if (manualTrigger) {
                const selectedProviderID = this.data.cluster.provider.id;
                const selectedRegionID = this.data.cluster.region.id;
                const selectedMachineTypeID = this.form.value.machineType?.vendorId;
                if (getPaymentOptions(payableProviders, selectedProviderID, selectedRegionID, selectedMachineTypeID).length > 0) {
                    this.snackbar.success("Payment method validated successfully");
                } else {
                    this.snackbar.warnPersistent(`No valid payment method found. Please contact a team administrator.`);
                }
            }
        });
    }

    estimateMonthlyPrice(snapshotConfig: SnapshotConfiguration, price: ClusterPrice) {
        const machineAndStoragePrice = this.data.cluster.status === "paused" || this.data.cluster.status === "pausing" ?
            price.paused.thousandthCentsPerHour :
            price.running.thousandthCentsPerHour;
        return this.pricePerHourToPerMonthString(machineAndStoragePrice);

        // let snapshotCount: number;
        // let snapshotChangeFactor: number;
        // if (snapshotConfig.frequency === "hourly") {
        //     snapshotCount = snapshotConfig.retentionDays * 24;
        //     snapshotChangeFactor = 0.01;
        // } else if (snapshotConfig.frequency === "daily") {
        //     snapshotCount = snapshotConfig.retentionDays;
        //     snapshotChangeFactor = 0.05;
        // } else if (snapshotConfig.frequency === "weekly") {
        //     snapshotCount = snapshotConfig.retentionDays/7;
        //     snapshotChangeFactor = 0.1;
        // }
        //
        // // estimate based on an underlying level of storage usage with subsequent snapshots being incremental
        // const estimatedSnapshotSizeBytes = this.data.currentStorageUsage + snapshotCount! * this.data.currentStorageUsage * snapshotChangeFactor!;
        // const estimatedSnapshotSizeGB = estimatedSnapshotSizeBytes/1e9;
        // const snapshotPrice = price.snapshots!.thousandthCentsPerHour * estimatedSnapshotSizeGB;
        // return this.pricePerHourToPerMonthString(machineAndStoragePrice + snapshotPrice);
    }

    updateToPaidMachineType() {
        if (!this.form.value.machineType!.isFree) return;
        this.form.patchValue({
            machineType: this.machineTypes.find(mt => mt.value.vendorId === this.form.value.machineType!.vendorId && !mt.value.isFree)!.value
        });
    }

    get formValuesChanged() {
        return this.machineTypeIsFreeChanged || this.machineTypeValueChanged || this.storageSizeChanged || this.snapshotFrequencyChanged || this.snapshotRetentionChanged;
    }

    get machineTypeIsFreeChanged() {
        const formMachineType = this.form.value.machineType;
        if (!formMachineType) return false;
        const cluster = this.data.cluster;
        return formMachineType.isFree !== cluster.isFree;
    }

    get machineTypeValueChanged() {
        const formMachineType = this.form.value.machineType;
        if (!formMachineType) return false;
        const cluster = this.data.cluster;
        return formMachineType.vendorId !== cluster.machineType.vendorId;
    }

    get storageSizeChanged() {
        const newStorageGB = storageSliderValueToGBMap[this.form.value.storageSlider!];
        return newStorageGB !== this.data.cluster.storageGB;
    }

    get snapshotFrequencyChanged() {
        return this.form.value.snapshotFrequency !== this.data.cluster.snapshotConfiguration.frequency;
    }

    get snapshotRetentionChanged() {
        return this.form.value.snapshotRetentionDays !== this.data.cluster.snapshotConfiguration.retentionDays;
    }

    submit() {
        this.mustAddPaymentMethod$.pipe(first()).subscribe(paymentMethodRequired => {
            if (paymentMethodRequired) this.openPaymentDetailsDialog();
            else this.updateClusterFromForm()
        })
    }

    updateClusterFromForm() {
        const update = this.buildUpdate();
        this.clusterApi.updateCluster(update).subscribe({
            next: () => {
                this.close("ok");
                this.snackbar.success(`Cluster updated successfully`);
            },
            error: () => {
                this.isSubmitting$.next(false);
            },
        });
    }

    openPaymentDetailsDialog() {
        this.dialog.open<PaymentDetailsModalComponent, any, DialogResult>(PaymentDetailsModalComponent).afterClosed().subscribe(result => {
            this.isSubmitting$.next(false);
            if (result === "ok") {
                this.fetchPayableProviders(true);
                this.snackbar.success("Payment method added successfully");
            }
            else if (result == null || result === "cancelled") return;
            else this.snackbar.errorPersistent(`Error setting up payment method: ${result.error}`);
        });
    }

    close(result?: DialogResult) {
        this.dialogRef.close(result);
    }

    protected readonly frequencyOptions = frequencyOptions;
    protected readonly toSentenceCase = toSentenceCase;
    protected readonly burstableTooltip = burstableTooltip;
}
